import { Link } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import { fadeIn, slideInLeft } from '~styles/animations';
import { COLORS, SURFACES } from '~styles/tokens';


export const BREAKPOINTS = {
  mobile: '768px',
  tablet: '1024px',
} as const;

export const SIDEBAR_WIDTH = {
  expanded: '15.125rem',
  collapsed: '4rem',
} as const;


const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateX(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateX(0);
  }
`;

export const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 998;
    animation: ${fadeIn} 0.2s ease-out;
  }
`;

interface SidebarContainerProps {
  $collapsed: boolean;
  $mobileOpen: boolean;
}

export const SidebarContainer = styled.aside<SidebarContainerProps>`
  display: flex;
  flex-direction: column;
  background-color: ${SURFACES.card};
  border-radius: 0.5rem;
  padding: 0.5rem 0;
  transition: width 0.3s ease;
  width: ${({ $collapsed }) =>
    $collapsed ? SIDEBAR_WIDTH.collapsed : SIDEBAR_WIDTH.expanded};
  flex-shrink: 0;
  overflow-y: ${({ $collapsed }) => ($collapsed ? 'visible' : 'auto')};
  overflow-x: visible;
  height: calc(100vh - 5rem);
  position: sticky;
  top: 2.5rem;

  &::-webkit-scrollbar {
    width: ${({ $collapsed }) => ($collapsed ? '0' : '4px')};
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLORS.borderStrong};
    border-radius: 4px;
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 999;
    border-radius: 0;
    max-height: 100vh;
    width: ${SIDEBAR_WIDTH.expanded};
    transform: ${({ $mobileOpen }) =>
      $mobileOpen ? 'translateX(0)' : 'translateX(-100%)'};
    animation: ${({ $mobileOpen }) =>
      $mobileOpen ? css`${slideInLeft} 0.3s ease-out` : 'none'};
    box-shadow: ${({ $mobileOpen }) =>
      $mobileOpen ? '4px 0 20px rgba(0, 0, 0, 0.15)' : 'none'};
  }
`;

export const NavWrapper = styled.nav`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const NavItemWrapper = styled.li<{ $collapsed: boolean }>`
  position: relative;

  &:hover {
    > div[data-flyout] {
      opacity: 1;
      visibility: visible;
      transform: translateX(0);
    }

    > div[data-tooltip] {
      opacity: 1;
      visibility: visible;
    }
  }
`;

export const Tooltip = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'flex' : 'none')};
  position: absolute;
  left: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
  background: #1a1a2e;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: #1a1a2e;
    border-left: none;
  }
`;

export const FlyoutMenu = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: calc(100% + 8px);
  top: 0;
  min-width: 200px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 1001;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-8px);
  transition: all 0.2s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 12px;
    border: 6px solid transparent;
    border-right-color: white;
    border-left: none;
  }
`;

export const FlyoutHeader = styled.div<{ $isOrange?: boolean }>`
  padding: 0.75rem 1rem;
  background: ${COLORS.primary};
  color: ${COLORS.white};
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const FlyoutItem = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 1rem;
  text-decoration: none;
  transition: all 0.15s ease;
  background: ${({ $active }) => ($active ? COLORS.danger : 'transparent')};
  border-left: 3px solid transparent;

  p {
    font-size: 0.8125rem;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textSecondary)};
    font-weight: ${({ $active }) => ($active ? '500' : '400')};
  }

  svg {
    width: 1rem;
    height: 1rem;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textTertiary)};
  }

  &:hover {
    background: ${({ $active }) => ($active ? COLORS.danger : COLORS.primary10)};

    p, svg {
      color: ${({ $active }) => ($active ? COLORS.white : COLORS.textPrimary)};
    }
  }
`;

interface NavGroupHeaderProps {
  $active: boolean;
  $expanded: boolean;
  $collapsed: boolean;
  $isOrange?: boolean;
  $nested?: boolean;
}

const collapsedItemStyles = css<{ $collapsed: boolean; $active: boolean }>`
  ${({ $collapsed }) => $collapsed && css`
    &:hover {
      transform: scale(1.05);

      & + ${Tooltip} {
        opacity: 1;
        visibility: visible;
      }
    }
  `}
`;

export const NavGroupHeader = styled.button<NavGroupHeaderProps>`
  display: flex;
  align-items: center;
  width: 100%;
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
  padding-right: 1.25rem;
  padding-left: ${({ $collapsed, $nested }) =>
    $collapsed ? '0' : $nested ? '2.5rem' : '1.25rem'};
  background-color: ${({ $active }) => ($active ? COLORS.primary : SURFACES.card)};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  gap: 0.625rem;
  min-height: 2.25rem;
  text-decoration: none;
  position: relative;

  p {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textPrimary)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textTertiary)};
    transition: transform 0.2s ease;
  }

  &:hover {
    background-color: ${({ $active }) => ($active ? COLORS.primary : COLORS.primary10)};

    ${({ $collapsed }) => $collapsed && css`
      transform: scale(1.08);
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    `}
  }

  &:active {
    ${({ $collapsed }) => $collapsed && css`
      transform: scale(0.98);
    `}
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.primary};
    outline-offset: -2px;
  }

  ${collapsedItemStyles}
`;

export const NavGroupLink = styled(Link)<NavGroupHeaderProps>`
  display: flex;
  align-items: center;
  width: 100%;
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
  padding-right: 1.25rem;
  padding-left: ${({ $collapsed, $nested }) =>
    $collapsed ? '0' : $nested ? '2.5rem' : '1.25rem'};
  background-color: ${({ $active }) => ($active ? COLORS.primary : SURFACES.card)};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  justify-content: ${({ $collapsed }) => ($collapsed ? 'center' : 'flex-start')};
  gap: 0.625rem;
  min-height: 2.25rem;
  text-decoration: none;
  position: relative;

  p {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textPrimary)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textTertiary)};
    transition: transform 0.2s ease;
  }

  &:hover {
    background-color: ${({ $active }) => ($active ? COLORS.primary : COLORS.primary10)};

    p, svg {
      color: ${({ $active }) => ($active ? COLORS.white : COLORS.textPrimary)};
    }

    ${({ $collapsed }) => $collapsed && css`
      transform: scale(1.08);
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    `}
  }

  &:active {
    ${({ $collapsed }) => $collapsed && css`
      transform: scale(0.98);
    `}
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.primary};
    outline-offset: -2px;
  }

  ${collapsedItemStyles}
`;

export const ChevronIcon = styled.span<{ $expanded: boolean; $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'flex')};
  margin-left: auto;
  transition: transform 0.2s;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0)')};

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

export const SubMenu = styled.ul<{ $collapsed: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0;
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'block')};
  background-color: ${SURFACES.inset};
`;

interface NavItemLinkProps {
  $active: boolean;
  $collapsed: boolean;
  $nestedChild?: boolean;
}

export const NavItemLink = styled(Link)<NavItemLinkProps>`
  display: flex;
  align-items: center;
  padding: 0.5rem 1.25rem 0.5rem ${({ $nestedChild }) => ($nestedChild ? '3.75rem' : '2.5rem')};
  background-color: ${({ $active }) => ($active ? COLORS.danger : 'transparent')};
  text-decoration: none;
  transition: all 0.2s;
  gap: 0.625rem;
  min-height: 2.125rem;

  p {
    font-size: 0.8125rem;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textPrimary)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  svg {
    flex-shrink: 0;
    color: ${({ $active }) => ($active ? COLORS.white : COLORS.textTertiary)};
    width: 1rem;
    height: 1rem;
  }

  &:hover {
    background-color: ${({ $active }) => ($active ? COLORS.danger : COLORS.primary10)};

    p, svg {
      color: ${({ $active }) => ($active ? COLORS.white : COLORS.textPrimary)};
    }
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.primary};
    outline-offset: -2px;
  }
`;

export const Shortcut = styled.kbd`
  display: none;
  font-family: inherit;
  font-size: 0.625rem;
  padding: 0.125rem 0.375rem;
  background-color: ${COLORS.neutral100};
  border-radius: 0.25rem;
  color: ${COLORS.textTertiary};
  margin-left: auto;

  @media (min-width: ${BREAKPOINTS.tablet}) {
    display: inline;
  }
`;

export const MobileCloseButton = styled.button`
  display: none;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    width: 2rem;
    height: 2rem;
    background: ${SURFACES.alternative};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: ${COLORS.textTertiary};

    &:hover {
      background: ${COLORS.neutral200};
      color: ${COLORS.textPrimary};
    }
  }
`;

export const SidebarHeader = styled.div`
  display: none;

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: flex;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid ${COLORS.borderDefault};
    margin-bottom: 0.5rem;
  }
`;

export const CollapseButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0.75rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${COLORS.textTertiary};
  transition: all 0.2s;

  &:hover {
    background-color: ${SURFACES.alternative};
    color: ${COLORS.textPrimary};
  }

  svg {
    transition: transform 0.3s ease;
    transform: ${({ $collapsed }) =>
      $collapsed ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  @media (max-width: ${BREAKPOINTS.mobile}) {
    display: none;
  }
`;
